/* eslint-disable @typescript-eslint/no-explicit-any */
// Account - Favourites Page -  Auth Page
import React from 'react';
import { GetServerSideProps } from 'next';
import { inspect } from 'util';
import { ApolloProvider } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

import { ResponsiveGrid } from '@adobe/aem-react-editable-components';

import { respGridUtil, getProcessEnvs, prePageCall, Layout, logger } from '@marriott/mi-headless-utils';

import { getDeployedEnvType, useMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';

import { constants } from '@marriott/mi-account-components/constants';

import {
  checkChinaLocale,
  fetchFlags,
  preProcessorCall,
  getDarkModeExperienceSegment,
  isACDLEnabled,
  updateModelJSON,
} from '@marriott/mi-account-components/utils';

import { CreateAccountPage1Model } from '../../../../_localDevMocks';
import operationSignatures from '../../../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH, SKIP_PREPROCESSOR_CALL, IS_LOCAL_DEV } = getProcessEnvs();
const { ITEM_PATH_ROOT, UNAUTH_URL, NEXT_PUBLIC_ENV_KEYS, EN_US, NON_EN_US } = constants;

global.loggerInstance = logger({}) as CallableFunction;
const { log } = global.loggerInstance('CreateAccountPage1');

export default function CreateAccountPage1({ model, deployedEnvType }) {
  const MiApolloClient = useMiApolloClient(operationSignatures, deployedEnvType);

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    authorMode,
    canonical,
  } = model;
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);

  return (
    <ApolloProvider client={MiApolloClient}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        gridCss={gridCss}
        isAuthorMode={authorMode}
        canonical={canonical}
      >
        <ResponsiveGrid
          {...responsiveGridModel}
          model={responsiveGridModel}
          pagePath={NEXT_PUBLIC_AEM_PATH}
          itemPath={ITEM_PATH_ROOT}
          customClassName="account-page-wrapper"
        />
      </Layout>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const CreateAccountPage1SpecificData = async request => {
  const deployedEnvType = getDeployedEnvType();
  let response: any = { operationSignatures: [], deployedEnvType };
  response = getDarkModeExperienceSegment(request, response);
  let featureFlags: any;
  const resolvedUrlString: string = request?.req?.url;
  const currentLocale =
    request?.req?.query?.locale ||
    (request?.req as any)?.locale ||
    request?.req?.headers?.['accept-language']?.split(',')?.[0]?.trim();
  const isChinaLocale = checkChinaLocale(currentLocale);
  const isAuthorMode = resolvedUrlString?.includes('isAuthorMode');
  const urlQueryParameters = new URLSearchParams(request?.query)?.toString();
  const preprocessorPath = urlQueryParameters ? `${UNAUTH_URL}?${urlQueryParameters}` : UNAUTH_URL;
  const hasSourceSystem = urlQueryParameters.includes('sourceSystem');

  featureFlags = await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_TARGET_ENABLED], {
    target: TARGETS.APP.ACCOUNT_RENDERER,
  });
  const isTargetEnabled = Boolean(featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_TARGET_ENABLED]);
  log.debug(
    `[CreateAccountPage1SpecificData] EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_TARGET_ENABLED: ${inspect(isTargetEnabled)}`
  );
  if (isTargetEnabled) {
    log.debug(
      `[CreateAccountPage1SpecificData] Skipping EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS fetch (target enabled)`
    );
  }

  if (!isChinaLocale && !hasSourceSystem && !isTargetEnabled) {
    log.debug(`[CreateAccountPage1SpecificData] header (id) - ${inspect(response?.componentVariation)}`);

    if (response?.componentVariation) {
      featureFlags = {
        ...featureFlags,
        [FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS]: response?.componentVariation,
      };
      log.debug(`[CreateAccountPage1SpecificData] featureFlag (componentVariation) - ${inspect(featureFlags)}`);
    } else {
      let uniqueId = request?.req?.cookies?.enrollDeviceId;
      if (!uniqueId) {
        uniqueId = uuidv4();
        const expiresDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
        const expiresStr = expiresDate.toUTCString();
        const cookieString = `enrollDeviceId=${uniqueId}; path=/; HttpOnly; Secure; SameSite=None; Expires=${expiresStr};`;

        const prev = request?.res?.getHeader('Set-Cookie');
        const next = Array.isArray(prev)
          ? [...prev, cookieString]
          : prev
          ? [prev as string, cookieString]
          : [cookieString];

        request?.res?.setHeader('Set-Cookie', next);
      }

      const normalizedLocale = currentLocale ? currentLocale.replace('_', '-').toLowerCase() : EN_US;
      const isEnUsLocale = normalizedLocale === EN_US;

      log.debug(
        `[CreateAccountPage1SpecificData] Fetching EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS (target disabled)`
      );
      const variationsFlags = await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS], {
        target: {
          ...TARGETS.APP.ACCOUNT_RENDERER,
          identifier: TARGETS.APP.ACCOUNT_RENDERER.identifier + `-${isEnUsLocale ? EN_US : NON_EN_US}`,
          name: uniqueId,
        },
      });
      featureFlags = { ...featureFlags, ...variationsFlags };
      log.debug(`[CreateAccountPage1SpecificData] UniqueId (id) - ${inspect(uniqueId)}`);
      if (featureFlags) {
        const ffValue = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS];
        log.debug(
          `[CreateAccountPage1SpecificData] FeatureFlags (fetchFlags) - experience_mcom_account_enrollment_variations: ${inspect(
            ffValue
          )}`
        );
      } else {
        log.warn('[CreateAccountPage1SpecificData] FeatureFlags (fetchFlags) returned empty/null');
      }
    }
    featureFlags = {
      ...featureFlags,
      ...(await fetchFlags([FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ACDL_ENABLED_PAGES_LOCALES], {
        target: TARGETS.APP.ACCOUNT_RENDERER,
      })),
    };
  }
  log.debug(`[CreateAccountPage1SpecificData] header (id) - ${inspect(request?.req?.headers)}`);

  // Log x-request-id and enrollment variation feature flag for debugging
  const xRequestId = request?.req?.headers?.['x-request-id'];
  const enrollmentVariation = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENROLLMENT_VARIATIONS];
  log.debug(
    `[CreateAccountPage1SpecificData] x-request-id: ${inspect(xRequestId)}, enrollment_variation: ${inspect(
      enrollmentVariation
    )}`
  );

  // Pass raw server timestamp - will be formatted in accountDataLayer.ts
  const serverTimestamp = new Date().toISOString();
  log.debug(`[CreateAccountPage1SpecificData] serverTimestamp: ${serverTimestamp}`);

  /**
   * Preprocessor Call Start
   */

  /** Bypassing preprocessor call intentionally for testing. It should be reverted back */
  if (SKIP_PREPROCESSOR_CALL !== 'true' && IS_LOCAL_DEV !== 'true' && !isAuthorMode) {
    // Making page specific preprocessor call
    log.debug(`[CreateAccountPage1SpecificData] session Id: ${request?.req?.cookies?.sessionID}`);
    const preprocessorResponse = await preProcessorCall(
      request?.req?.cookies?.sessionID,
      request?.req,
      preprocessorPath,
      {}
    );
    log.debug(`[CreateAccountPage1SpecificData] PreprocessorResponse: ${inspect(preprocessorResponse)}`);
    if (preprocessorResponse?.data?.error) {
      const error = {
        redirect: { redirect: preprocessorResponse?.data?.redirect },
        value: true,
      };
      response = {
        ...response,
        operationSignatures: [],
        deployedEnvType,
        error,
      };
    } else if (preprocessorResponse?.data) {
      response = {
        ...response,
        operationSignatures: [],
        deployedEnvType,
        nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
      };
    }
  }
  /**
   * Preprocessor Call End
   */
  return { ...response, featureFlags: featureFlags ?? null, serverTimestamp };
};

const postAEMModelOperations = async (
  ...args: [unknown, unknown, unknown, Record<string, unknown>, Record<string, Record<string, string>>]
) => {
  let [, , , model] = args;
  const [, , , , pageSpecificData] = args;

  const IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL = isACDLEnabled(
    model,
    pageSpecificData?.['featureFlags']?.['experience_mcom_account_acdl_enabled_pages_locales'],
    'createAccount'
  );

  if (!IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL) {
    // means dca components shouldnt be shown on page
    model = updateModelJSON(model, [
      'mi-aem-common-spa/components/content/dcadecisioncontainer',
      'mi-aem-common-spa/components/content/tilecarouselcontainer',
    ]);
  }

  return {
    ...model,
    IS_CURRENT_LOCALE_AND_PAGE_ENABLED_WITH_ACDL,
    modelJsonUpdated: true, // jjust for testing log
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  CreateAccountPage1SpecificData,
  CreateAccountPage1Model,
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
